import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useRbSystemAlert } from '@scmlatam/web-ui';
import { employeeService } from '../services/service';
import { usePermisoEmpleado as usePermiso } from '../../../../auth/permisosModulos';
import type { AccrualProfileRow, AccrualProfileRowValidationErrors, SelectOption, UseFormAssignAccrualProfile, UseFormAssignAccrualProfileProps } from '../types/hooks';
import type { Alert, EmployeeAccrualProfilePayload } from '../types/services';

const INDEFINITE_DATE = dayjs('3000-01-01');

const emptyRowErrors: AccrualProfileRowValidationErrors = {
    accrualProfileId: '',
    startDate: '',
    endDate: '',
};

const getRowKey = (row: AccrualProfileRow) =>
    `${row.employeeAccrualProfileId ?? 'new'}-${row.accrualProfileId}-${row.startDate?.format('YYYY-MM-DD') ?? ''}-${row.endDate?.format('YYYY-MM-DD') ?? ''}`;

const useFormAssignAccrualProfile = ({ rowsSelected, option, setOption, reload }: UseFormAssignAccrualProfileProps): UseFormAssignAccrualProfile => {
    const { t } = useTranslation('workers.form.assign_accrual_profile');
    const { showAlert, closeAlert } = useRbSystemAlert();
    const canAssignAccrualProfile = usePermiso('regla-acumulacion');
    const idCounter = useRef(0);

    const [openForm, setOpenForm] = useState(false);
    const [profileOptions, setProfileOptions] = useState<SelectOption[]>([]);
    const [rows, setRows] = useState<AccrualProfileRow[]>([]);
    const [initialRows, setInitialRows] = useState<AccrualProfileRow[]>([]);
    const [searchProfile, setSearchProfile] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    const [validationMessage, setValidationMessage] = useState('');
    const [showMessage, setShowMessage] = useState(false);
    const [validationErrors, setValidationErrors] = useState<Record<number, AccrualProfileRowValidationErrors>>({});

    const selectedEmployee = rowsSelected[0] ?? null;
    const selectedWorkerLabel = selectedEmployee ? `${selectedEmployee.person_number} - ${selectedEmployee.fullname}` : '';

    const alert: Alert = {
        t,
        showAlert: (severity, message) => showAlert(severity, message),
        closeAlert,
    };

    const buildNewRow = (): AccrualProfileRow => {
        idCounter.current += 1;
        return {
            id: idCounter.current,
            employeeAccrualProfileId: null,
            employeeId: selectedEmployee?.id ?? '',
            accrualProfileId: '',
            startDate: null,
            endDate: null,
        };
    };

    const resetForm = () => {
        setRows([]);
        setInitialRows([]);
        setProfileOptions([]);
        setSearchProfile('');
        setValidationMessage('');
        setValidationErrors({});
        setShowMessage(false);
        idCounter.current = 0;
    };

    const closeForm = () => {
        setOpenForm(false);
        setOption(null);
        resetForm();
    };

    const hasChanges = () => JSON.stringify(rows.map(getRowKey)) !== JSON.stringify(initialRows.map(getRowKey));

    const handleCancel = () => {
        if (hasChanges()) {
            setShowMessage(true);
            return;
        }

        closeForm();
    };

    const sortRowsByDate = (list: AccrualProfileRow[]): AccrualProfileRow[] => {
        return [...list].sort((a, b) => {
            const aHasStart = Boolean(a.startDate?.isValid());
            const bHasStart = Boolean(b.startDate?.isValid());

            if (!aHasStart && !bHasStart) return a.id - b.id;
            if (!aHasStart) return 1;
            if (!bHasStart) return -1;
            return a.startDate!.valueOf() - b.startDate!.valueOf();
        });
    };

    const loadData = async () => {
        if (!selectedEmployee) return;

        setLoadingData(true);
        const [{ profiles: availableProfiles }, { profiles: existingProfiles }] = await Promise.all([
            employeeService.getAccrualProfiles({ alert }),
            employeeService.getEmployeeAccrualProfiles({
                employeeId: selectedEmployee.id,
                alert,
            }),
        ]);

        setProfileOptions(availableProfiles);

        const loadedRows = existingProfiles.map((profile, index) => ({
            id: index + 1,
            employeeAccrualProfileId: profile.accrual_profile_r_employee_id,
            employeeId: String(profile.employee_id),
            accrualProfileId: String(profile.accrual_profile_id),
            startDate: dayjs(profile.start_date),
            endDate: dayjs(profile.end_date),
        }));

        idCounter.current = loadedRows.length;
        const nextRows = sortRowsByDate(loadedRows);

        setRows(nextRows);
        setInitialRows(nextRows);
        validateRows(nextRows, false);
        setLoadingData(false);
    };

    const addRow = () => {
        setRows((currentRows) => {
            const updated = [...currentRows, buildNewRow()];
            validateRows(updated, false);
            return updated;
        });
    };

    const removeRow = (rowId: number) => {
        setRows((currentRows) => {
            const updated = currentRows.filter((row) => row.id !== rowId);
            validateRows(updated, false);
            return updated;
        });
    };

    const updateRow = (rowId: number, changes: Partial<Omit<AccrualProfileRow, 'id' | 'employeeId'>>) => {
        setRows((currentRows) => {
            const updated = currentRows.map((row) => (row.id === rowId ? { ...row, ...changes } : row));
            const sorted = changes.startDate !== undefined || changes.endDate !== undefined ? sortRowsByDate(updated) : updated;

            validateRows(sorted, false);
            return sorted;
        });
    };

    const validateRows = (targetRows: AccrualProfileRow[] = rows, isSubmit = false) => {
        const nextErrors: Record<number, AccrualProfileRowValidationErrors> = {};
        let firstErrorMessage = '';

        targetRows.forEach((row, index) => {
            const rowErrors: AccrualProfileRowValidationErrors = { ...emptyRowErrors };

            if (isSubmit && !row.accrualProfileId) {
                rowErrors.accrualProfileId = t('validation.profile_required');
            }

            if (isSubmit && !row.startDate?.isValid()) {
                rowErrors.startDate = t('validation.start_date_required');
            }

            if (isSubmit && !row.endDate?.isValid()) {
                rowErrors.endDate = t('validation.end_date_required');
            }

            if (row.startDate?.isValid() && row.endDate?.isValid()) {
                if (row.startDate.isAfter(row.endDate, 'day')) {
                    rowErrors.endDate = t('validation.date_range_invalid');
                    if (!firstErrorMessage) {
                        firstErrorMessage = t('validation.date_range_invalid');
                    }
                }

                const hasIndefinite = targetRows.some((currentRow, currentIndex) => currentIndex !== index && Boolean(currentRow.endDate && currentRow.endDate.year() >= INDEFINITE_DATE.year()));

                if (hasIndefinite && row.endDate.isSame(INDEFINITE_DATE, 'day')) {
                    rowErrors.endDate = t('validation.indefinite_exists');
                    if (!firstErrorMessage) {
                        firstErrorMessage = t('validation.indefinite_exists');
                    }
                }

                const overlappingRow = targetRows.find((currentRow, currentIndex) => {
                    if (currentIndex === index || !currentRow.startDate?.isValid() || !currentRow.endDate?.isValid()) {
                        return false;
                    }

                    const isStartOverlapping = row.startDate!.isBefore(currentRow.endDate, 'day') || row.startDate!.isSame(currentRow.endDate, 'day');
                    const isEndOverlapping = row.endDate!.isAfter(currentRow.startDate, 'day') || row.endDate!.isSame(currentRow.startDate, 'day');

                    return isStartOverlapping && isEndOverlapping;
                });

                if (overlappingRow) {
                    rowErrors.endDate = t('validation.date_overlap');
                    if (!firstErrorMessage) {
                        firstErrorMessage = t('validation.date_overlap');
                    }
                }
            }

            if (Object.values(rowErrors).some(Boolean)) {
                nextErrors[row.id] = rowErrors;
            }
        });

        setValidationErrors(nextErrors);

        if (firstErrorMessage) {
            setValidationMessage(firstErrorMessage);
        } else if (!isSubmit) {
            setValidationMessage('');
        }

        return Object.keys(nextErrors).length === 0;
    };

    const submit = async () => {
        if (!canAssignAccrualProfile || !selectedEmployee) return;

        if (!validateRows(rows, true)) {
            setValidationMessage((currentMsg) => currentMsg || t('alerts.validation_error'));
            return;
        }

        const payload: EmployeeAccrualProfilePayload[] = rows.map((row) => ({
            accrual_profile_r_employee_id: row.employeeAccrualProfileId,
            accrual_profile_id: Number(row.accrualProfileId),
            employee_id: Number(row.employeeId),
            start_date: row.startDate!.format('YYYY-MM-DD'),
            end_date: row.endDate!.format('YYYY-MM-DD'),
        }));

        const toCreate = payload.filter((profile) => profile.accrual_profile_r_employee_id === null).map(({ accrual_profile_r_employee_id: _, ...profile }) => profile);
        const toUpdate = payload.filter((profile) => profile.accrual_profile_r_employee_id !== null);

        setLoading(true);
        const [created, updated] = await Promise.all([
            toCreate.length > 0 ? employeeService.createEmployeeAccrualProfiles({ body: toCreate, alert }) : Promise.resolve(true),
            toUpdate.length > 0 || rows.length === 0
                ? employeeService.updateEmployeeAccrualProfiles({
                      employeeId: selectedEmployee.id,
                      body: toUpdate,
                      alert,
                  })
                : Promise.resolve(true),
        ]);

        if (created && updated) {
            showAlert('success', t('alerts.success'));
            await reload();
            closeForm();
        }

        setLoading(false);
    };

    useEffect(() => {
        if (option !== 'assign_accrual_profile') return;

        if (!canAssignAccrualProfile) {
            setOption(null);
            return;
        }

        if (rowsSelected.length !== 1) {
            setValidationMessage(t('validation.one_worker'));
            setOption(null);
            return;
        }

        setOpenForm(true);
        setOption(null);
    }, [option, rowsSelected.length, setOption, t, canAssignAccrualProfile]);

    useEffect(() => {
        if (openForm) {
            loadData();
        }
    }, [openForm]);

    return {
        t,
        title: t('title'),
        openForm,
        rowsSelected,
        selectedWorkerLabel,
        profileOptions,
        rows,
        searchProfile,
        setSearchProfile,
        loading,
        loadingData,
        validationMessage,
        setValidationMessage,
        showMessage,
        setShowMessage,
        validationErrors,
        hasChanges,
        closeForm,
        handleCancel,
        addRow,
        removeRow,
        updateRow,
        submit,
    };
};

export { useFormAssignAccrualProfile };
